import React from 'react'
import { api, type CardAccount } from '../api/client'
import { useApp } from '../context/AppContext'
import { useCardAccount } from '../payments/useCardAccount'
import { canTakeCardPayments, cardPaymentsShown } from '../payments/visibility'
import { bookingLink } from '../booking/bookingLink'
import { useBilling } from '../billing/useBilling'
import { checklistView, ready, setupSteps, type SetupInput, type SetupStep } from './setup'
import { Icon } from '../ui/Icon'
import ClientNoticeSheet from './ClientNoticeSheet'

// cardStage is where Stripe's setup stands, for the card step's line.
function cardStage(account: CardAccount | null): SetupInput['cardStage'] {
  if (account?.status === 'onboarding' || account?.status === 'restricted') return 'details'
  if (account?.status === 'in_review') return 'checking'
  return undefined
}

const shareKey = (pro: string) => `harmovi.shareCardDone.${pro}`
const optionalKey = (pro: string) => `harmovi.optionalSetupDismissed.${pro}`

// "Get ready to take bookings" on Today (design review epic 5): until a client
// can book her, this says what's left; each step opens its Settings and ticks
// itself off when she comes back. Then it offers her link once, and goes.
export default function SetupChecklist({ onActive }: { onActive?: (active: boolean) => void }) {
  const { state, dispatch } = useApp()
  const account = useCardAccount()
  const { billing, coachLocked } = useBilling()
  const [input, setInput] = React.useState<Omit<SetupInput, 'cardReady' | 'cardShown' | 'cardApproved' | 'cardStage' | 'goalShown' | 'noticeChecked'> | null>(null)
  const [slug, setSlug] = React.useState('')
  const [copied, setCopied] = React.useState(false)
  const pro = state.me?.professional.id ?? ''
  const [shareDone, setShareDone] = React.useState(() => {
    try { return localStorage.getItem(shareKey(pro)) === '1' } catch { return false }
  })
  const [optionalDismissed, setOptionalDismissed] = React.useState(() => {
    try { return localStorage.getItem(optionalKey(pro)) === '1' } catch { return false }
  })
  const dismissOptional = () => {
    setOptionalDismissed(true)
    try { localStorage.setItem(optionalKey(pro), '1') } catch { /* a convenience only */ }
  }
  const [noticeOpen, setNoticeOpen] = React.useState(false)
  // The notice step opens its sheet. Every other step with no Settings panel
  // is the goal, on the Goals tab.
  const open = (s: SetupStep) => {
    if (s.id === 'notice') setNoticeOpen(true)
    else if (s.panel) dispatch({ type: 'OPEN_SETTINGS_PANEL', payload: s.panel })
    else dispatch({ type: 'SET_ACTIVE_TAB', payload: 'goals' })
  }
  const row = (s: SetupStep) => (
    <li key={s.id}>
      <button type="button" className={s.done ? 'setup-step setup-step--done' : 'setup-step'} onClick={() => open(s)}>
        <span className="setup-step__mark" aria-hidden="true">{s.done && <Icon name="check" size={14} />}</span>
        <span className="setup-step__text">
          <span className="setup-step__title">{s.title}{s.done && <span className="visually-hidden"> (done)</span>}</span>
          <span className="setup-step__detail">{s.detail}</span>
        </span>
        <Icon name="chevron-right" size={18} className="setup-step__go" />
      </button>
    </li>
  )

  // Reload whenever Settings closes, so a step she just finished ticks.
  const settingsOpen = state.settingsPanel !== null
  React.useEffect(() => {
    if (settingsOpen) return
    let live = true
    // The plan is asked separately: a goal is optional, so if it can't be
    // read the checklist still shows, with the goal step not ticked.
    Promise.all([api.getBusiness(), api.listServices(), api.getWorkingHours(), api.getPlan().catch(() => null)])
      .then(([b, services, hours, plan]) => {
        if (!live) return
        setSlug(b?.slug ?? '')
        setInput({ business: b ?? null, services: services.length, hours: hours.length, hasGoal: !!plan?.goal })
      })
      .catch(() => {})
    return () => { live = false }
  }, [settingsOpen, state.activeTab])

  // Ticked by her session: an older API has no such field, and no such
  // step to leave undone, so only a null counts as not yet checked.
  const noticeChecked = state.me?.professional.client_notice_checked_at !== null
  const steps = input ? setupSteps({ ...input, noticeChecked, cardReady: canTakeCardPayments(account), cardShown: cardPaymentsShown(account), cardApproved: billing?.rung === 'free' && account?.status === 'active', cardStage: cardStage(account), goalShown: !coachLocked }) : null
  const isReady = steps ? ready(steps) : true
  const showShare = steps !== null && isReady && !shareDone && slug !== '' && !!input?.business?.published
  React.useEffect(() => { onActive?.(steps !== null && !isReady) }, [steps !== null, isReady, onActive])

  if (!steps) return null

  const view = checklistView(steps, optionalDismissed)
  const optionalList = view && view.optional.length > 0 && (
    <div className="setup-optional">
      <h3 className="setup-optional__title">Optional</h3>
      <ul className="setup-list setup-list--compact">{view.optional.map(row)}</ul>
      {isReady && (
        <button type="button" className="btn btn-quiet btn-compact" onClick={dismissOptional}>
          Not now
        </button>
      )}
    </div>
  )

  if (!isReady && view) {
    return (
      <section className="setup-card" aria-labelledby="setup-h">
        <h2 id="setup-h">Get ready to take bookings</h2>
        <p className="setup-progress-text">{view.done} of {view.required.length} done</p>
        <div className="setup-progress" aria-hidden="true"><span style={{ width: `${(view.done / view.required.length) * 100}%` }} /></div>
        <ol className="setup-list">{view.required.map(row)}</ol>
        {optionalList}
        {noticeOpen && <ClientNoticeSheet onClose={() => setNoticeOpen(false)} />}
      </section>
    )
  }

  const optionalCard = optionalList && (
    <section className="setup-card setup-card--compact" aria-labelledby="optional-h">
      <h2 id="optional-h" className="visually-hidden">Still to set up</h2>
      {optionalList}
    </section>
  )

  if (!showShare) return optionalCard || null
  const link = bookingLink(slug)
  const finish = () => {
    setShareDone(true)
    try { localStorage.setItem(shareKey(pro), '1') } catch { /* a convenience only */ }
  }
  return (
    <>
    <section className="setup-card" aria-labelledby="share-h">
      <h2 id="share-h">Your booking page is on</h2>
      <p className="setup-progress-text">Share it in your Instagram bio, on WhatsApp, or anywhere clients find you.</p>
      <p className="setup-link">{link}</p>
      <div className="setup-actions">
        <button type="button" className="btn btn-primary btn-compact" onClick={() => {
          navigator.clipboard?.writeText(link).then(() => setCopied(true)).catch(() => {})
        }}>{copied ? 'Copied' : 'Copy link'}</button>
        <button type="button" className="btn btn-quiet btn-compact" onClick={finish}>Done</button>
      </div>
    </section>
    {optionalCard}
    </>
  )
}
